import React from 'react';

import { css } from '@emotion/react';
import { CssVars, NavigationRow, NeutralColorToken, SemanticColorToken, Spinner24, Type } from '@robinhood/design';
import Cookies from 'js-cookie';
import { graphql } from 'relay-runtime';

import PrivacyNoticeModal from './PrivacyNoticeModal';
import VermontModal from './VermontModal';
import logger from 'src/clients/LoggingClient';
import { TermsAndConditionsPageMutation as TermsAndConditionsPageMutationType } from 'src/clients/Network/gql/build/TermsAndConditionsPageMutation.graphql';
import { Domain, fetch } from 'src/clients/Network/http/fetch';
import { Event, track } from 'src/clients/tracking';
import Button from 'src/components/creditApplication/Button';
import Cell from 'src/components/creditApplication/Cell';
import CreditApplicationPageLayout from 'src/components/creditApplication/CreditApplicationPageLayout';
import {
  CkBadgeCookieKey,
  CkPayloadCookieKey,
  CkSubIdCookieKey,
  NavigationContext,
  X1InviteCookieKey,
} from 'src/components/creditApplication/NavigationProvider';
import { showGlobalErrorAlert, useGlobalStore } from 'src/state/global';
import { useAsyncMutation } from 'src/utils/graphql';

const TermsUrl = 'https://api.robinhood.com/creditcard/legal/terms';

const TermsAndConditionsPageMutation = graphql`
  mutation TermsAndConditionsPageMutation($q: CreateCreditApplicationRequest!) {
    createCreditApplication(q: $q) {
      data {
        creditApplication {
          id
        }
      }
      errors
    }
  }
`;

const TermsAndConditionsPage = () => {
  const { underwrite, underwriteLoading, setCurrentPage } = React.useContext(NavigationContext);
  const isVermont = useGlobalStore(
    (s) => s.personalInformation.residentialAddress?.subdivision?.toLowerCase() === 'vt',
  );
  const personalInformation = useGlobalStore((s) => s.personalInformation);
  const creditApplicationId = useGlobalStore((s) => s.creditApplicationId);
  const ckSubId = useGlobalStore((s) => s.ckSubId);
  const ckBadge = useGlobalStore((s) => s.ckBadge);
  const ckPayload = useGlobalStore((s) => s.ckPayload);
  const x1Invite = useGlobalStore((s) => s.x1Invite);
  const [htmlContent, setHtmlContent] = React.useState<string>('');
  const [termsLoading, setTermsLoading] = React.useState(true);
  const [error, setError] = React.useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = React.useState(false);
  const [showVermontModal, setShowVermontModal] = React.useState(false);
  const iframeRef = React.useRef<HTMLIFrameElement>(null);
  const [createApplicationCommit, createApplicationLoading] =
    useAsyncMutation<TermsAndConditionsPageMutationType>(TermsAndConditionsPageMutation);
  const loading = createApplicationLoading || underwriteLoading;

  const buttonState = React.useMemo(() => {
    return loading ? 'loading' : !termsLoading && !error ? 'default' : 'disabled';
  }, [loading, termsLoading, error]);

  const loadTerms = React.useCallback(async () => {
    setTermsLoading(true);
    setError(false);
    try {
      const response = await fetch(Domain.RHCreditCard, '/legal/terms?inline=true');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const html = await response.text();
      // Reference: https://github.com/robinhoodmarkets/robinhood-trader-android/blob/2866f8f0868f9816b5977b2dd287cf6fe7c2db0e/trader/credit-card/feature-credit-card/src/main/java/com/robinhood/android/creditcard/ui/creditapplication/terms/TermsComposable.kt#L213
      const scriptToInject = `
        <script>            
          // Create viewport meta tag
          var meta = document.createElement('meta');
          meta.name = 'viewport';
          meta.content = 'initial-scale=1.0, user-scalable=no';
          var head = document.getElementsByTagName('head')[0];
          head.appendChild(meta);
          
          // Add custom CSS styling
          var cssString = \`
            :root {
              --fg: #D9D8D3;
              --fg3: #6E695E;
              --bg2: #1C1A17;
              --accent: #CFB97D;
            }
            body {
              color: var(--fg);
              background-color: var(--bg2);
              font-family: sans-serif;
              font-size: 12px;
              padding-left: 10px;
              padding-right: 10px
            }
            a {
              color: var(--accent);
              word-wrap: break-word;
            }
            td {
              padding: 8px 0;
              border-bottom: 1px solid var(--fg3);
            }
            tr:last-child td {
              border-bottom: 1px solid transparent;
            }
            pre {
              white-space: normal;
            }
            table {
              table-layout: fixed;
              width: 100%;
              border-collapse: collapse;
            }
            h3 {
              word-wrap: break-word;
            }
            tbody {
              vertical-align: top;
            }
          \`;
          
          var style = document.createElement('style');
          style.innerHTML = cssString;
          document.head.appendChild(style);
        </script>
      `;

      let modifiedHtml;
      if (html.includes('</body>')) {
        modifiedHtml = html.replace('</body>', scriptToInject + '</body>');
      } else {
        modifiedHtml = html + scriptToInject;
      }

      setHtmlContent(modifiedHtml);
      setTermsLoading(false);
    } catch (error) {
      logger.error(error, { source: 'TermsAndConditionsPage.loadTerms' });
      setError(true);
      setTermsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    loadTerms();
  }, [loadTerms]);

  const onShowPrivacyModal = React.useCallback(() => {
    setShowPrivacyModal(true);
  }, []);

  const onDismissPrivacyModal = React.useCallback(() => {
    setShowPrivacyModal(false);
  }, []);

  const onShowVermontModal = React.useCallback(() => {
    setShowVermontModal(true);
  }, []);

  const onDismissVermontModal = React.useCallback(() => {
    setShowVermontModal(false);
  }, []);

  const onContinue = React.useCallback(async () => {
    if (!!creditApplicationId) {
      track(Event.RhApplicationTermsAndConditionsAccepted, { creditApplicationId });
      await underwrite();
      return;
    }
    if (
      !personalInformation.firstName ||
      !personalInformation.lastName ||
      !personalInformation.email ||
      !personalInformation.e164 ||
      !personalInformation.dateOfBirth ||
      !personalInformation.ssn ||
      !personalInformation.incomeMicro ||
      !personalInformation.residentialAddress?.primaryAddressLine ||
      !personalInformation.residentialAddress.locality ||
      !personalInformation.residentialAddress.subdivision ||
      !personalInformation.residentialAddress.zip5
    ) {
      showGlobalErrorAlert();
      return;
    }
    // Prefer X1 invite over CK sub ID and badge
    const attribution =
      !!x1Invite || !!Cookies.get(X1InviteCookieKey)
        ? {
            x1InviteSlug: x1Invite ?? Cookies.get(X1InviteCookieKey),
          }
        : {
            creditKarmaTrackingId: ckSubId ?? Cookies.get(CkSubIdCookieKey),
            creditKarmaLightboxBadge: ckBadge ?? Cookies.get(CkBadgeCookieKey),
            creditKarmaTrackingPayload: ckPayload ?? Cookies.get(CkPayloadCookieKey),
          };
    const res = await createApplicationCommit({
      variables: {
        q: {
          firstName: personalInformation.firstName,
          lastName: personalInformation.lastName,
          email: personalInformation.email,
          e164: personalInformation.e164,
          dateOfBirth: personalInformation.dateOfBirth,
          ssn: personalInformation.ssn,
          address: {
            primaryAddressLine: personalInformation.residentialAddress.primaryAddressLine,
            secondaryAddressLine: personalInformation.residentialAddress.secondaryAddressLine,
            locality: personalInformation.residentialAddress.locality,
            subdivision: personalInformation.residentialAddress.subdivision,
            zip5: personalInformation.residentialAddress.zip5,
          },
          incomeMicro: personalInformation.incomeMicro,
          serviceLevel: 'RH_GOLD',
          forceAddressOverride: false,
          closeAuthorizedUserOverride: false,
          attribution,
        },
      },
    });
    if (res.createCreditApplication.errors.length > 0) {
      if (res.createCreditApplication.errors.some((error) => error === 'APPLICATION_COLLISION')) {
        setCurrentPage('ACCOUNT_ALREADY_EXISTS');
      } else {
        showGlobalErrorAlert();
      }
    } else if (res.createCreditApplication.data?.creditApplication?.id) {
      useGlobalStore.setState({
        creditApplicationId: res.createCreditApplication.data.creditApplication.id,
      });
      track(Event.RhApplicationTermsAndConditionsAccepted, {
        creditApplicationId: res.createCreditApplication.data.creditApplication.id,
      });
      await underwrite(res.createCreditApplication.data.creditApplication.id);
    }
  }, [underwrite, personalInformation, creditApplicationId, ckSubId, ckBadge, createApplicationCommit]);

  return (
    <CreditApplicationPageLayout title='Gold Card Terms & Conditions'>
      <div css={root}>
        <Cell overrideRootStyles={cell}>
          <NavigationRow
            title='Privacy notices'
            titleSize='S'
            overrideRootStyles={navRow}
            hasDivider={false}
            action='click'
            onClick={onShowPrivacyModal}
          />
          {isVermont && (
            <>
              <div css={divider} />
              <NavigationRow
                title='Vermont lending laws'
                titleSize='S'
                overrideRootStyles={navRow}
                hasDivider={false}
                action='click'
                onClick={onShowVermontModal}
              />
            </>
          )}
        </Cell>
        <Cell overrideRootStyles={cell}>
          <div css={row}>
            <Type.Text size='S'>Disclosures and Agreements</Type.Text>
          </div>
          <div css={termsContainer}>
            {termsLoading ? (
              <Spinner24 color={SemanticColorToken.Positive} />
            ) : error ? (
              <>
                <Type.Text size='S' color={SemanticColorToken.Error}>
                  Failed to load terms and conditions
                </Type.Text>
                <Button label='Retry' type='reset' onClick={loadTerms} />
              </>
            ) : (
              <iframe ref={iframeRef} srcDoc={htmlContent} width='100%' height='100%' />
            )}
          </div>
          <NavigationRow
            title='View as PDF'
            titleSize='S'
            overrideRootStyles={navRow}
            hasDivider={false}
            action='link'
            newTab
            href={TermsUrl}
            ctaIcon='PopOut16'
          />
        </Cell>
        <Type.Text size='M' color={NeutralColorToken.Fg2} overrideRootStyles={textCenter}>
          By continuing, you agree to the following disclosures and a “soft pull” of your credit report. This will not
          affect your credit score.
        </Type.Text>
        <Button label='Agree' state={buttonState} width='full' type='submit' onClick={onContinue} />
      </div>
      {showVermontModal && <VermontModal onDismiss={onDismissVermontModal} />}
      {showPrivacyModal && <PrivacyNoticeModal onDismiss={onDismissPrivacyModal} />}
    </CreditApplicationPageLayout>
  );
};

const root = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const cell = css`
  padding: 0px;
  overflow: hidden;
`;

const row = css`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: 16px;
  gap: 16px;
`;

const navRow = css`
  padding-left: 16px;
  padding-right: 16px;
`;

const divider = css`
  border-bottom: 1px solid ${CssVars.neutral.bg3};
`;

const termsContainer = css`
  border-top: 1px solid ${CssVars.neutral.bg3};
  border-bottom: 1px solid ${CssVars.neutral.bg3};
  min-height: 200px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
`;

const textCenter = css`
  text-align: center;
`;

export default TermsAndConditionsPage;
